/**
 * <sticky-header> — the site header.
 *
 * Two responsibilities:
 *
 *  1. Scroll state. The header starts transparent over the hero; once the page scrolls
 *     past `data-threshold` pixels it gains `.is-scrolled`, which paints it with the
 *     brand colour and swaps in the inverse logo. An IntersectionObserver on a zero-height
 *     sentinel does the detection, so nothing runs on the scroll event itself.
 *
 *  2. The mobile navigation drawer, including focus trapping and Escape to close.
 *
 * Registered as a custom element so the behaviour is scoped to the markup that needs it
 * and survives section re-rendering in the theme editor.
 */

// Wrapped so nothing here reaches global scope: these are classic scripts, and a
// top-level name shared with another file makes the whole file throw before it can
// register its element. Also what makes a duplicate <script> tag for this file safe.
(() => {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector('[data-menu-toggle]');
      this.closeButton = this.querySelector('[data-menu-close]');
      this.drawer = this.querySelector('[data-menu-drawer]');

      this.onKeydown = this.onKeydown.bind(this);
      this.openDrawer = this.openDrawer.bind(this);
      this.closeDrawer = this.closeDrawer.bind(this);

      this.toggleButton?.addEventListener('click', this.openDrawer);
      this.closeButton?.addEventListener('click', this.closeDrawer);
      this.drawer?.addEventListener('click', (event) => {
        // Clicking the scrim (the drawer element itself, outside its panel) closes.
        if (event.target === this.drawer) this.closeDrawer();
      });

      this.watchScroll();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.sentinel?.remove();
      document.removeEventListener('keydown', this.onKeydown);
    }

    /* ---- Scroll state ---- */

    watchScroll() {
      const threshold = Number(this.dataset.threshold ?? 40);

      // A sentinel of the threshold's height, placed at the very top of the document.
      // While it is visible we are above the threshold; once it scrolls out, we are past it.
      this.sentinel = document.createElement('div');
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.sentinel.style.cssText = `position:absolute;top:0;left:0;width:1px;height:${Math.max(threshold, 1)}px;pointer-events:none;`;
      document.body.prepend(this.sentinel);

      this.observer = new IntersectionObserver(
        ([entry]) => this.classList.toggle('is-scrolled', !entry.isIntersecting),
        { threshold: 0 }
      );

      this.observer.observe(this.sentinel);
    }

    /* ---- Drawer ---- */

    get isOpen() {
      return this.classList.contains('is-menu-open');
    }

    openDrawer() {
      if (!this.drawer) return;

      this.drawer.hidden = false;
      // Force a frame so the transition runs from the closed state.
      requestAnimationFrame(() => this.classList.add('is-menu-open'));

      this.toggleButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);

      this.closeButton?.focus();
    }

    closeDrawer() {
      if (!this.drawer || !this.isOpen) return;

      this.classList.remove('is-menu-open');
      this.toggleButton?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);

      const hide = () => {
        this.drawer.hidden = true;
      };

      // Wait for the slide-out unless motion is reduced, in which case there is none.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        hide();
      } else {
        this.drawer.addEventListener('transitionend', hide, { once: true });
      }

      this.toggleButton?.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      if (event.key !== 'Tab' || !this.isOpen) return;

      const focusable = [...this.drawer.querySelectorAll(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', StickyHeader);
  }
})();
